import {
  CleaningServices,
  FeaturedPlayList,
  MoreVert,
  PlayArrow,
  Settings,
  SwapHoriz,
} from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, MenuItem, Menu as MuiMenu } from '@mui/material';
import type { ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useClearDeviceCache, useDeleteDevice } from '~/api/devices';
import { IconButton } from '~/components/IconButton';
import { ActionButton } from '~/components/button';
import { TransferDialog, useTransferDeviceDialog } from '~/components/devices/TransferDialog';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreAction,
  MoreActionLink,
  MoreDeleteAction,
  mergeGridActionProps,
  tableActionButtonProps,
  type GridActionCellProps,
} from '~/components/table';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import type { DeviceList__Device as Device } from '../../queries/list.generated';

export interface MenuProps extends GridActionCellProps {
  buttonType?: 'action' | 'icon';
  device: Device;
  Icon?: ElementType;
}

export const Menu = ({ buttonType = 'icon', device, Icon = MoreVert, ...cellProps }: MenuProps) => {
  const { t } = useTranslation();
  const { currentUser, currentNetwork } = useAppContext();
  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<Device>();
  const deviceActionProps = mergeGridActionProps(moreTableActionProps(device), cellProps);
  const [openTransferDialog, transferDialogProps] = useTransferDeviceDialog();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [confirmClearCache, confirmClearCacheProps] = useConfirmDialog();
  const [deleteDevice] = useDeleteDevice();
  const [clearDeviceCache] = useClearDeviceCache();

  return (
    <>
      {buttonType === 'icon' ? (
        <IconButton
          label={t('pages.devices.menu.actions')}
          color="secondary"
          {...tableActionButtonProps(deviceActionProps)}
          size="small"
        >
          <Icon />
        </IconButton>
      ) : (
        <ActionButton {...deviceActionProps} />
      )}
      <MuiMenu {...moreMenuProps}>
        {(currentUser.admin || currentNetwork.canManage.value) && (
          <MoreActionLink
            Icon={FeaturedPlayList}
            onClick={moreActionProps.closeMenu}
            title={t('pages.devices.menu.manifest')}
            target="_blank"
            to={`/devices/${moreMenuProps.context?.id}/manifest.json`}
          />
        )}
        {currentUser.admin && (
          <MoreAction
            {...moreActionProps}
            Icon={CleaningServices}
            title={t('pages.devices.menu.clear_cache')}
            onClick={async () => {
              assert(moreMenuProps.context != null, 'clear cache: no device context');
              if (!(await confirmClearCache())) return;
              await clearDeviceCache({
                variables: { input: { deviceId: moreMenuProps.context.id } },
              });
            }}
          />
        )}
        <MenuItem component={Link} to={`${moreMenuProps.context?.id}/preview`} state={true}>
          <ListItemIcon>
            <PlayArrow />
          </ListItemIcon>
          <ListItemText>{t('common.labels.preview')}</ListItemText>
        </MenuItem>
        {moreMenuProps.context?.canUpdate.value && (
          <MenuItem component={Link} to={`${moreMenuProps.context.id}/settings`} state={true}>
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            <ListItemText>{t('common.labels.settings')}</ListItemText>
          </MenuItem>
        )}
        {/* Separate conditionals: MenuList arrow keys only reach direct children. */}
        {currentUser.admin && (
          <MoreAction
            {...moreActionProps}
            Icon={SwapHoriz}
            title={t('pages.devices.menu.transfer')}
            onClick={() => {
              assert(moreMenuProps.context != null, 'transfer: no device context');
              openTransferDialog(moreMenuProps.context);
            }}
          />
        )}
        {currentUser.admin && <Divider />}
        {currentUser.admin && (
          <MoreDeleteAction
            {...moreActionProps}
            onClick={async () => {
              assert(moreMenuProps.context != null, 'delete: no device context');
              if (!(await confirmDelete())) return;
              await deleteDevice({
                variables: { deviceId: moreMenuProps.context.id },
              });
              moreMenuProps.onClose();
            }}
          />
        )}
      </MuiMenu>
      <TransferDialog {...transferDialogProps} />
      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.devices.menu.delete_device_confirm')}
        deleteConfirm
        prompt={
          <span>
            {t('pages.devices.menu.delete_device_prompt')}{' '}
            <b>{t('common.warnings.cannot_be_undone')}</b>
          </span>
        }
        title={t('pages.devices.menu.delete_device')}
      />
      <ConfirmDialog
        {...confirmClearCacheProps}
        confirm={t('pages.devices.bulk_actions.confirms.clear_cache.confirm')}
        prompt={t('pages.devices.bulk_actions.confirms.clear_cache.prompt')}
        title={t('pages.devices.bulk_actions.confirms.clear_cache.title')}
      />
    </>
  );
};
